/**
 * WebMCP integration — exposes a tiny, safe surface to AI agents
 * via the experimental navigator.modelContext API.
 *
 * Spec: https://webmachinelearning.github.io/webmcp/
 *
 * Only safe, public, side-effect-free (or navigation-only) tools are exposed.
 * Nothing that costs credits, requires auth, or touches the database.
 */

type ModelContextTool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (input: unknown) => Promise<unknown> | unknown;
};

type ModelContextNavigator = Navigator & {
  modelContext?: {
    provideContext: (ctx: { tools: ModelContextTool[] }) => void | Promise<void>;
  };
};

const PUBLIC_PAGES: Record<string, string> = {
  home: "/",
  faq: "/faq",
  pricing: "/pricing",
  suporte: "/suporte",
  termos: "/termos",
  privacidade: "/privacidade",
  cookies: "/cookies",
};

const PRICING_INFO = `PromptCodex — planos com 200 créditos acumulativos cada (validade 60 dias).
- Mensal: cobrança recorrente mensal.
- Semestral: cobrança a cada 6 meses, melhor custo-benefício de médio prazo.
- Anual: cobrança anual, maior desconto.
Pagamento via cartão de crédito ou boleto (Pagar.me).
Detalhes e valores atuais: https://promptcodex.com.br/pricing`;

let registered = false;

export function registerWebMcp(): void {
  if (registered) return;
  if (typeof window === "undefined") return;

  const nav = navigator as ModelContextNavigator;
  if (!nav.modelContext || typeof nav.modelContext.provideContext !== "function") {
    return;
  }

  const tools: ModelContextTool[] = [
    {
      name: "open_promptcodex_page",
      description:
        "Navigate the current PromptCodex tab to one of the public pages: home, faq, pricing, suporte, termos, privacidade, cookies.",
      inputSchema: {
        type: "object",
        properties: {
          page: {
            type: "string",
            enum: Object.keys(PUBLIC_PAGES),
            description: "Public page identifier",
          },
        },
        required: ["page"],
        additionalProperties: false,
      },
      execute: (input) => {
        const page = (input as { page?: string } | undefined)?.page;
        if (!page || !(page in PUBLIC_PAGES)) {
          return { ok: false, error: "Unknown page" };
        }
        window.location.href = PUBLIC_PAGES[page];
        return { ok: true, navigatedTo: PUBLIC_PAGES[page] };
      },
    },
    {
      name: "get_promptcodex_pricing_info",
      description:
        "Return a short text summary of PromptCodex subscription plans, credits and payment methods. Read-only.",
      inputSchema: {
        type: "object",
        properties: {},
        additionalProperties: false,
      },
      execute: () => ({ summary: PRICING_INFO }),
    },
  ];

  try {
    void nav.modelContext.provideContext({ tools });
    registered = true;
  } catch {
    // Silently ignore — feature is best-effort, must never break the app.
  }
}
